import React, { useRef, useState } from "react";
import { Plus, Calendar as CalendarIcon, Tag, Bell, Mic, Square, Loader2, Flag, Repeat } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useApp } from "@/context/AppContext";
import { CATEGORIES, PRIORITIES, REPEATS } from "@/lib/translations";
import { useVoiceRecorder } from "@/hooks/useVoiceRecorder";
import { localToIso } from "@/lib/datetime";

export const AddTaskForm = ({ onAdd }) => {
  const { t, lang } = useApp();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("none");
  const [priority, setPriority] = useState("medium");
  const [repeat, setRepeat] = useState("none");
  const [dueDate, setDueDate] = useState("");
  const [reminder, setReminder] = useState("");
  const inputRef = useRef(null);

  const { recording, processing, toggle, supported } = useVoiceRecorder({
    lang,
    onError: t("voiceError"),
    onResult: (text) => {
      setTitle((prev) => (prev ? `${prev} ${text}` : text));
      inputRef.current?.focus();
    },
  });

  const reset = () => {
    setTitle("");
    setCategory("none");
    setPriority("medium");
    setRepeat("none");
    setDueDate("");
    setReminder("");
  };

  const submit = async () => {
    const value = title.trim();
    if (!value) {
      toast.error(t("emptyError"));
      inputRef.current?.focus();
      return;
    }
    await onAdd({
      title: value,
      category: category === "none" ? null : category,
      priority,
      repeat,
      due_date: localToIso(dueDate),
      reminder_at: localToIso(reminder),
    });
    reset();
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    } else if (e.key === "Escape") {
      reset();
    }
  };

  const selectClass = "h-10 rounded-xl bg-background";

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="add-task-form">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            data-testid="task-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={recording ? t("listening") : processing ? t("transcribing") : t("addPlaceholder")}
            className="w-full h-12 bg-background border border-border rounded-xl pl-4 pr-12 text-base outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
          />
          {supported && (
            <button
              data-testid="voice-btn"
              onClick={toggle}
              disabled={processing}
              aria-label={t("voiceHint")}
              className={`absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                recording
                  ? "bg-destructive text-destructive-foreground animate-pulse"
                  : "text-muted-foreground hover:text-primary hover:bg-secondary"
              }`}
            >
              {processing ? (
                <Loader2 className="w-[18px] h-[18px] animate-spin" />
              ) : recording ? (
                <Square className="w-[18px] h-[18px]" />
              ) : (
                <Mic className="w-[18px] h-[18px]" />
              )}
            </button>
          )}
        </div>
        <button
          data-testid="add-task-btn"
          onClick={submit}
          className="h-12 px-5 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:brightness-110 active:scale-[0.98] transition-all shadow-sm shrink-0"
        >
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">{t("addButton")}</span>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-muted-foreground shrink-0" />
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="category-select" className={selectClass}>
              <SelectValue placeholder={t("category")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t("noCategory")}</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c.key} value={c.key} data-testid={`category-option-${c.key}`}>
                  <span className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                    {t(c.labelKey)}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Flag className="w-4 h-4 text-muted-foreground shrink-0" />
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger data-testid="priority-select" className={selectClass}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRIORITIES.map((p) => (
                <SelectItem key={p.key} value={p.key} data-testid={`priority-option-${p.key}`}>
                  <span className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
                    {t(p.labelKey)}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Repeat className="w-4 h-4 text-muted-foreground shrink-0" />
          <Select value={repeat} onValueChange={setRepeat}>
            <SelectTrigger data-testid="repeat-select" className={selectClass}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REPEATS.map((r) => (
                <SelectItem key={r.key} value={r.key} data-testid={`repeat-option-${r.key}`}>
                  {t(r.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-4 h-4 text-muted-foreground shrink-0" />
          <input
            type="datetime-local"
            data-testid="due-date-input"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            title={t("dueTime")}
            className="h-10 w-full bg-background border border-border rounded-xl px-3 text-sm outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
          />
        </div>
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-muted-foreground shrink-0" />
          <input
            type="datetime-local"
            data-testid="reminder-input"
            value={reminder}
            onChange={(e) => setReminder(e.target.value)}
            title={t("reminder")}
            className="h-10 w-full bg-background border border-border rounded-xl px-3 text-sm outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
          />
        </div>
      </div>
    </div>
  );
};
